import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Space, Switch } from 'antd';
import { useIntl } from 'react-intl';

import AccountsCostCentersGeneralForm from '../Form';
import { CostCenter } from '@/core/domain/enterprise/CostCenter/CostCenter.types';
import { SaveButton } from '@/components/_common';
import {
  useActiveOrInactiveCostCenter,
  useCostCenterById,
} from '@/core/domain/enterprise/CostCenter/CostCenter.hooks';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';

interface Props {
  costCenter?: CostCenter;
  visible: boolean;
  loading: boolean;
  onSubmit: (value: CostCenter) => void;
  onCancel: () => void;
}
function AccountsCostCentersGeneralModalForm({
  costCenter,
  visible,
  loading,
  onSubmit,
  onCancel,
}: Props) {
  const intl = useIntl();

  const [form] = Form.useForm();
  const [activated, setActivated] = useState<boolean>();
  const [fetchLoading, setFetchLoading] = useState(true);
  const { mutateAsync } = useCostCenterById();
  const { mutateAsync: activeMutate } = useActiveOrInactiveCostCenter();

  useEffect(() => {
    setFetchLoading(true);
    if (costCenter) {
      mutateAsync(costCenter.id)
        .then(
          ({ active, description }) => (
            setActivated(active), form.setFieldsValue({ description })
          ),
        )
        .then(() => setFetchLoading(false));
    }
  }, [visible, costCenter]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      onSubmit(values);
      form.resetFields();
      onCancel();
    } catch {}
  };

  const handleActive = async (costCenterId: number) => {
    setFetchLoading(true);
    try {
      return activeMutate(costCenterId)
        .then(() => setActivated(!activated))
        .then(() => setFetchLoading(false));
    } catch {
      setFetchLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      visible={visible}
      confirmLoading={loading}
      title={
        costCenter
          ? intl.formatMessage({ id: 'costCenters.modal.general.edit.title' })
          : intl.formatMessage({ id: 'costCenters.modal.general.new.title' })
      }
      onOk={handleSubmit}
      onCancel={handleCancel}
      footer={
        <Space>
          {costCenter && (
            <Switch
              unCheckedChildren={intl.formatMessage({
                id: 'commons.entity.inactive',
              })}
              checkedChildren={intl.formatMessage({
                id: 'commons.entity.active',
              })}
              checked={activated}
              loading={fetchLoading}
              onChange={() => handleActive(costCenter.id)}
            />
          )}

          <Button onClick={handleCancel}>
            {intl.formatMessage({ id: 'commons.form.button.close' })}
          </Button>

          <SaveButton
            onClick={handleSubmit}
            disabled={
              !hasPermissions([Authorities.REGISTER_COST_CENTER_ADD]) ||
              !hasPermissions([Authorities.REGISTER_COST_CENTER_EDIT])
            }
            loading={loading}
          >
            {intl.formatMessage({ id: 'commons.form.button.save' })}
          </SaveButton>
        </Space>
      }
    >
      <AccountsCostCentersGeneralForm form={form} />
    </Modal>
  );
}

export default AccountsCostCentersGeneralModalForm;
